import { useEffect, useState } from "react";

interface RelatedApplication {
  platform: string;
  id?: string;
  url?: string;
  version?: string;
}

type NavigatorWithRelatedApps = Navigator & {
  getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
};

/**
 * 이 오리진에서 설치된 앱(WebAPK)이 실제로 존재하는지.
 *
 * `null` 은 "확인 불가" 이지 "미설치" 가 아니다 — 이 둘을 섞으면 안 된다.
 * iOS Safari 에는 API 자체가 없어 영원히 null 이고, 그 환경에서 "미설치" 로
 * 단정하면 이미 설치한 사람에게 설치를 계속 권하게 된다.
 *
 * 동작 조건: Chromium 계열(Android·데스크톱) + 매니페스트의 `related_applications`
 * 에 자기 자신이 `platform: "webapp"` 으로 등록돼 있어야 한다. 등록이 없으면
 * 설치돼 있어도 빈 배열이 온다.
 */
export function useInstalledApp(): boolean | null {
  const [installed, setInstalled] = useState<boolean | null>(null);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const nav = navigator as NavigatorWithRelatedApps;
    if (typeof nav.getInstalledRelatedApps !== "function") return;

    let cancelled = false;
    // iframe·비보안 컨텍스트에서는 reject 된다 — 그 경우 null(미확인) 로 남긴다.
    nav
      .getInstalledRelatedApps()
      .then((apps) => {
        if (!cancelled) setInstalled(apps.some((app) => app.platform === "webapp"));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  return installed;
}
